










































































































import Vue from 'vue'
import Authenticated from './helpers/Authenticated.vue'
import StaffOnly from './helpers/StaffOnly.vue'
import rooms, { Room } from '@/services/Rooms'
import GuestBox from '@/components/staff/GuestBox.vue'
import ArtistVideoAdmin from '@/components/staff/ArtistVideoAdmin.vue'
import RestaurantSettings from '@/components/staff/RestaurantSettings.vue'
import agora from '@/services/Agora'
import seatings, { SeatingType } from '@/services/Seatings'
import Guests, { Guest, GuestList, Timer } from '@/services/Guests'
import firebaseApp, { getUserId } from '@/firebaseApp'
import ArtistVideoBox from '@/components/staff/ArtistVideoBox.vue'
import ParticipateButton from '@/components/staff/ParticipateButton.vue'
import SaveStaffName from '@/components/staff/SaveStaffName.vue'
import ListOfRooms from '@/components/staff/ListOfRooms.vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import EventBus, { eventTypes } from '@/utils/EventBus'
import Loader from '@/components/global/loader.vue'

type TimerList = {
  [index: string]: Timer
}

@Component({
  components: {
    Loader,
    ListOfRooms,
    SaveStaffName,
    ParticipateButton,
    ArtistVideoBox,
    Authenticated,
    StaffOnly,
    GuestBox,
    RestaurantSettings,
    ArtistVideoAdmin,
  },
  beforeDestroy() {
    agora.destroy()
  },
})
export default class StaffScreen extends Vue {
  @Prop({ required: true }) public restaurantId!: string

  public rooms = rooms
  public agora = agora
  public roomsList: Room[] = []
  public guests: GuestList = {}
  public waiterId: string | null = null
  public waiterName: string | null = null
  public activeSeating: SeatingType | null = null
  public activeRoomId: string | null = null
  public switchingGuestId: string | null = null
  public activeGuest: Guest | null = null
  public isParticipating = false
  public modalRestaurantSettingsActive = false
  public timerList: TimerList = {}

  // Everything for the timers
  public interval: number = 0

  public async created() {
    await agora.isReady()
    await this.getWaiterName()

    // Get active seating
    this.activeSeating = await seatings.getActiveSeating(this.restaurantId)
    this.waiterId = await getUserId()

    rooms.observeRooms(this.restaurantId, updatedRooms => {
      this.roomsList = updatedRooms
    })

    Guests.observeActiveGuests(
      this.restaurantId,
      this.activeSeating?.id + '',
      async updatedGuestList => {
        if (!updatedGuestList) {
          return
        }

        this.guests = updatedGuestList
        this.updateTimerList()
      }
    )

    // we always start in the lobby
    // now init that room
    this.initRoom()

    // init the everything for the timers
    EventBus.$on(eventTypes.startTimer, this.addTimer)
    EventBus.$on(eventTypes.stopTimer, this.removeTimer)
  }

  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  public updateTimerList() {
    if (!this.guests) {
      return
    }

    this.timerList = Object.keys(this.guests).reduce((result, key) => {
      const now = new Date().getTime()
      const timer = this.guests[key] && this.guests[key].timer
      if (timer && timer.endsOn > now) {
        timer.userId = key
        result[key] = timer
      }

      return result
    }, {} as TimerList)

    if (!this.timerList || Object.keys(this.timerList).length === 0) {
      clearInterval(this.interval)
    } else {
      this.setTimerGameLoop()
    }
  }

  public addTimer(timer: Timer) {
    Guests.addTimer(this.restaurantId, this.activeSeatingId, timer.userId, timer)
  }

  public removeTimer(userId: string) {
    Guests.addTimer(this.restaurantId, this.activeSeatingId, userId, null)
  }

  public setTimerGameLoop() {
    this.interval = setInterval(() => {
      this.gameLoop()
    }, 1000)
  }

  public gameLoop() {
    const now = new Date().getTime()
    Object.keys(this.timerList).forEach((guestId: string) => {
      const timer = this.timerList[guestId]
      const timerFinished = timer.endsOn <= now
      if (timerFinished && this.guests[guestId]?.timer) {
        Guests.finishTimer(this.restaurantId, this.activeSeatingId, guestId)
      }
    })
  }

  public async showRoom(roomId: string | null, switchingGuestId: string | null = null) {
    this.activeRoomId = roomId
    this.switchingGuestId = switchingGuestId
  }

  public movedGuestToRoom(roomId: string | null, guestId: string) {
    this.showRoom(roomId, guestId)
  }

  public manageRestaurantSettings() {
    // Always go back to the lobby, as that's the only room not manageable
    this.showRoom(null)

    // Activate the modal
    this.modalRestaurantSettingsActive = true
  }

  public async getWaiterName() {
    const getStaffName = firebaseApp.functions('europe-west1').httpsCallable('getStaffName')
    try {
      const result = await getStaffName()
      this.waiterName = result.data.displayName
    } catch (err) {
      console.error('Ophalen niet gelukt', err)
    }
  }

  public async initRoom() {
    const roomLabel = this.activeRoomId || 'lobby'
    const channelId = `${this.restaurantId}:${this.activeSeatingId}:${roomLabel}`
    await agora.join(channelId, `waiter-${this.waiterId}`, this.switchingGuestId)
  }

  public guestsInRoom(roomId: string) {
    return Object.keys(this.guests).reduce((r, guestId) => {
      const guest = this.guests[guestId]
      if (guest.room === roomId) {
        r[guestId] = guest
      }
      return r
    }, {} as { [index: string]: Guest })
  }

  @Watch('activeRoomId')
  public async handleActiveRoomIdChange() {
    await this.initRoom()
  }

  public get activeSeatingId(): string {
    return this.activeSeating
      ? this.activeSeating?.id + ''
      : this.$t('staff.sidebar.noneSeatingId') + ''
  }

  public get nrOfGuests() {
    return Object.keys(this.guests).length
  }

  public get activeRoomGuests() {
    if (this.activeRoomId === null) {
      return this.guestsWithoutRoom
    }

    return this.guestsInRoom(this.activeRoomId)
  }

  public get guestsWithoutRoom() {
    return Object.keys(this.guests).reduce((r, guestId) => {
      if (!this.guests[guestId].room) {
        r[guestId] = this.guests[guestId]
      }
      return r
    }, {} as { [index: string]: Guest })
  }

  public get numberOfGuestsPerRoom() {
    return this.roomsList.reduce((r: any, room: Room) => {
      r[room.id] = Object.keys(this.guestsInRoom(room.id)).length
      return r
    }, {} as { [index: string]: number })
  }

  public get activeRoom(): Room {
    return this.roomsList.find((r: Room) => r.id === this.activeRoomId) || ({} as Room)
  }

  public get showParticipateBtn() {
    return (
      this.activeRoomId &&
      !this.activeRoom.private &&
      this.activeRoomGuests &&
      Object.keys(this.activeRoomGuests).length > 0
    )
  }
}
